import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Dropdown, type DropdownMenuItem } from "zhihao-ui-react";
import { useLocation, useNavigate } from "react-router";
import { useUserStore, type ReactPermissionRoute } from "@/stores/react";
import { resolveActiveMenuPath } from "@/views/layouts/menu/activeMenu";

interface MeasuredMenu extends ReactPermissionRoute {
    width: number;
}

const getMenuTitle = (menu: ReactPermissionRoute) => {
    const title = menu.meta?.title;
    return typeof title === "string" ? title : menu.name || menu.path || "";
};

const findFirstVisibleRoute = (routes: ReactPermissionRoute[]): ReactPermissionRoute | null => {
    for (const route of routes) {
        if (route.meta?.hidden === true) {
            continue;
        }
        if (!route.children?.length) {
            return route;
        }
        const child = findFirstVisibleRoute(route.children);
        if (child) {
            return child;
        }
    }
    return null;
};

/** React 一级菜单，替代旧 firstMenu.vue 的响应式拆分与路由跳转逻辑。 */
export function FirstMenu() {
    const location = useLocation();
    const navigate = useNavigate();
    const menuList = useUserStore(state => state.menuList);
    const containerRef = useRef<HTMLDivElement>(null);
    // 保存可见菜单节点，供活动背景按旧 Vue 版本精确定位。
    const menuRefs = useRef<Record<string, HTMLButtonElement | null>>({});
    const moreMenuRef = useRef<HTMLButtonElement>(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [slideStyle, setSlideStyle] = useState({
        left: "0px",
        width: "0px",
        transition: "none"
    });

    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        const observer = new ResizeObserver(entries => {
            setContainerWidth(entries[0]?.contentRect.width || 0);
        });
        observer.observe(container);
        setContainerWidth(container.clientWidth);
        return () => observer.disconnect();
    }, []);

    const measuredMenus = useMemo<MeasuredMenu[]>(() => {
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        if (context) {
            context.font = "14px sans-serif";
        }
        return menuList
            .filter(menu => menu.meta?.hidden !== true)
            .map(menu => ({
                ...menu,
                width: (context?.measureText(getMenuTitle(menu)).width || getMenuTitle(menu).length * 14) + 16
            }));
    }, [menuList]);

    const { visibleMenus, moreMenus } = useMemo(() => {
        const moreWidth = 86;
        const totalWidth = measuredMenus.reduce((sum, menu) => sum + menu.width, 0);
        if (!containerWidth || totalWidth + moreWidth <= containerWidth) {
            return { visibleMenus: measuredMenus, moreMenus: [] as MeasuredMenu[] };
        }

        let visibleWidth = totalWidth;
        const more: MeasuredMenu[] = [];
        for (
            let index = measuredMenus.length - 1;
            index >= 0 && visibleWidth + moreWidth > containerWidth;
            index -= 1
        ) {
            const menu = measuredMenus[index];
            if (!menu) {
                continue;
            }
            more.unshift(menu);
            visibleWidth -= menu.width;
        }
        return {
            visibleMenus: measuredMenus.slice(0, measuredMenus.length - more.length),
            moreMenus: more
        };
    }, [containerWidth, measuredMenus]);

    const activeMenuPath = useMemo(() => {
        const selectedPath = resolveActiveMenuPath(menuList, location.pathname);
        const firstPath = `/${selectedPath.split("/").filter(Boolean)[0] || ""}`;
        return menuList.find(menu => menu.path === firstPath)?.path || firstPath;
    }, [location.pathname, menuList]);

    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        // 活动菜单进入“更多”时，背景跟随“更多”按钮，保持旧版的活动域提示。
        const activeMenu = menuRefs.current[activeMenuPath] || moreMenuRef.current;
        if (!activeMenu) {
            return;
        }

        const containerRect = container.getBoundingClientRect();
        const menuRect = activeMenu.getBoundingClientRect();
        setSlideStyle({
            left: `${menuRect.left - containerRect.left}px`,
            width: `${menuRect.width}px`,
            transition: "all 0.2s ease-in-out"
        });
    }, [activeMenuPath, visibleMenus, moreMenus]);

    const goToMenu = (menu: ReactPermissionRoute) => {
        const firstRoute = findFirstVisibleRoute(menu.children || []);
        const target = firstRoute?.path || menu.path;
        if (target) {
            void navigate(target);
        }
    };

    const moreItems: DropdownMenuItem[] = moreMenus.map(menu => ({
        key: menu.path || menu.name || getMenuTitle(menu),
        label: getMenuTitle(menu),
        onClick: () => goToMenu(menu)
    }));

    return (
        <div ref={containerRef} className="header-first-menu">
            <div className="slide-background" style={slideStyle} aria-hidden="true" />
            <div className="visible-menu-block">
                {visibleMenus.map(menu => {
                    const isActive = menu.path === activeMenuPath;
                    return (
                        <button
                            key={menu.path || menu.name}
                            type="button"
                            className={isActive ? "menu active" : "menu"}
                            ref={node => {
                                if (menu.path) {
                                    menuRefs.current[menu.path] = node;
                                }
                            }}
                            onClick={() => goToMenu(menu)}
                        >
                            {getMenuTitle(menu)}
                        </button>
                    );
                })}
            </div>
            {moreMenus.length ? (
                <Dropdown className="more-menu-wrap" trigger={["click"]} menu={{ items: moreItems }}>
                    <button ref={moreMenuRef} type="button" className="more-menu">
                        更多
                    </button>
                </Dropdown>
            ) : null}
        </div>
    );
}

export default FirstMenu;
